import { DIMENSION_AVATAR_SMALL, DIMENSION_AVATAR_VERY_SMALL } from '../../../constants';
import { UserInfo } from '../../../types/UserInfoTypes';
import { AvatarPicture } from '../../avatar/AvatarPicture';
import UserSummaryBasic from '../../common/invite-users/UserSummaryBasic';
import { getIsUserInactive } from '../../../lib/getIsUserActive';
import { useMemo, useRef } from 'react';
import { useFeatureToggle } from '../../../feature-flags/useFeatureToggle';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import { colors, IconButton, Typography } from '@sweep-io/sweep-design';
import { ChevronDown } from '@sweep-io/sweep-design/dist/icons';
import WithNotificationDot from '../../common/WithNotificationDot';
import { isEllipsisActive } from '../../../lib/isEllipsisActive';

interface UserSummaryProps {
  user?: UserInfo;
  isActive?: boolean;
  hasUnreadNotifications?: boolean;
}

const UserSummaryForHeader = ({
  user = {},
  isActive,
  hasUnreadNotifications,
}: UserSummaryProps) => {
  const { status } = user || {};
  const avatar = useMemo(() => ({ emoji: user.emoji, imageUrl: user.imageUrl }), [user]);
  const { multiAccounts } = useFeatureToggle();
  const nameRef = useRef<HTMLSpanElement>(null);
  const accountRef = useRef<HTMLSpanElement>(null);
  const isNameEllipsisActive = isEllipsisActive(nameRef.current);
  const isAccountEllipsisActive = isEllipsisActive(accountRef.current);
  const hasEllipsis = isNameEllipsisActive || isAccountEllipsisActive;

  return (
    <UserSummaryBasic
      className={`header-user-summary ${isActive ? 'active' : ''}`}
      sx={{
        ...(multiAccounts
          ? {
              backgroundColor: isActive ? colors.grey[100] : undefined,
              '&:hover': { backgroundColor: colors.grey[100] },
              p: '3px 8px 3px 12px',
              borderRadius: '8px',
              maxWidth: '236px',
              overflow: 'hidden',
              minWidth: 0,
            }
          : {}),
      }}
    >
      <div className="user-summary-avatar" data-testid="user-summary-avatar-header">
        <Stack alignItems="center" justifyContent="center" direction="row" gap={1} minWidth={0}>
          <WithNotificationDot showDot={hasUnreadNotifications ?? false}>
            <AvatarPicture
              avatar={avatar}
              dimension={multiAccounts ? DIMENSION_AVATAR_VERY_SMALL : DIMENSION_AVATAR_SMALL}
              clickable={true}
              isInactive={getIsUserInactive(status)}
            />
          </WithNotificationDot>
          {multiAccounts && (
            <Stack direction="row" alignItems="center" justifyContent="center" gap={1} minWidth={0}>
              <Tooltip
                disableHoverListener={!hasEllipsis}
                title={
                  <Stack>
                    <Typography variant="body" color={colors.white}>
                      {user.name ?? user.email}
                    </Typography>
                    <Typography variant="body" color={colors.grey[500]}>
                      Account: {user.account?.name}
                    </Typography>
                  </Stack>
                }
              >
                <Stack maxWidth="160px">
                  <Stack minWidth={0} width="100%">
                    <Typography ref={nameRef} variant="body" color={colors.grey[900]} ellipsis>
                      {user.name ?? user.email}
                    </Typography>
                    <Typography
                      ref={accountRef}
                      variant="caption"
                      color={colors.grey[700]}
                      ellipsis
                    >
                      {user.account?.name}
                    </Typography>
                  </Stack>
                </Stack>
              </Tooltip>
              <IconButton variant="flat" size="tiny">
                <ChevronDown />
              </IconButton>
            </Stack>
          )}
        </Stack>
      </div>
    </UserSummaryBasic>
  );
};

export { UserSummaryForHeader };
